import { Flex } from '@chakra-ui/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { PropTypes } from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useMountedState } from 'react-use';
import { Waypoint } from 'react-waypoint';
import clientApi from '~/clientApi';
import Button from '~/components/Button';
import LinkButton from '~/components/LinkButton';
import {
  ModalNoResults,
  reelsSortingOptions as sortingOptions,
} from '~/components/RecentContentFiltersBar';
import Spinner from '~/components/Spinner';
import StoryPreviewCard from '~/components/StoryPreviewCard';
import { CONTENT_TYPES, SOCIAL_PLATFORMS } from '~/constants';
import { CONTENTS_COLLECTION_QUERY_KEY } from '~/queries/keys';
import { selectContentModalTypes } from '~/types/content';
import { pluralize } from '~/utils';

import { Modal } from '../Modal';
import {
  DesktopModalFiltersBar as ModalFiltersBar,
  OpenFiltersButton,
} from '../RecentContentFiltersBar';
import { SelectContentGrid } from '../SelectContentGrid';
import { SelectedContentPreview } from '../SelectedContentPreview';

// duplicated in many places. not sure if consistent across all codebase though!
const API_DATE_FORMAT = 'YYYY-MM-DD';
const PAGE_SIZE = 15;

const propTypes = {
  ...selectContentModalTypes,
  titles: PropTypes.shape({
    modal: PropTypes.node,
    selectedPreview: PropTypes.node,
  }),
  creatorId: PropTypes.string,
};

const fetchInitialContent = async (ids, creatorId) => {
  const promises = ids.map((id) =>
    clientApi.content.read({ id, _impersonateOnce: creatorId }),
  );
  return await Promise.all(promises);
};

const defaultTitles = {
  modal: 'Choose your Instagram Reels Highlights',
  selectedPreview: 'Selected highlights:',
};

/* This is the Modal from new MP
 *
 * TODO: It is necessary to refactor this part and
 * change to typescript all Select for the all Social Media
 * and the components related with this part.
 */
export const SelectReelsModal = ({
  cancelButtonText = 'Cancel',
  confirmButtonText = 'Save Changes',
  disabledItemIds = [],
  fetchInitialContentFn = fetchInitialContent,
  initialSelectedItemIds = [],
  isOpen = false,
  maxItems = 6,
  minItems = 3,
  onCancel,
  onClose,
  onSubmit,
  shouldShowSelectionPreview,
  titles: titlesProp = {},
  creatorId,
}) => {
  const titles = { ...defaultTitles, ...titlesProp };
  const [sortBy, setSortBy] = useState(sortingOptions[0]);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFiltersBoxVisible, setIsFiltersBoxVisible] = useState(false);
  const [selectedItems, setSelectedItems] = useState(initialSelectedItemIds);
  const [selectedContent, setSelectedContent] = useState([]);
  const isMounted = useMountedState();

  const filtersApplied = !!startDate || !!endDate;

  const queryParams = {
    channel: 'reels',
    pageSize: PAGE_SIZE,
    order: sortBy?.value,
    'postedAt[after]': startDate
      ? startDate.format(API_DATE_FORMAT)
      : undefined,
    'postedAt[before]': endDate
      ? // If we don't send a time, the BE will consider T00:00:00 by default
        `${endDate.format(API_DATE_FORMAT)}T23:59:59`
      : undefined,
  };

  const {
    status,
    data = {
      pages: [
        {
          results: [],
        },
      ],
    },
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    [CONTENTS_COLLECTION_QUERY_KEY, queryParams],
    ({ pageParam: page = 1 }) =>
      clientApi.content.index({
        ...queryParams,
        page,
        _impersonateOnce: creatorId,
      }),
    {
      getNextPageParam: (lastGroup, allGroups) =>
        lastGroup?.results?.length >= PAGE_SIZE && allGroups.length + 1,
    },
  );

  const reels = data.pages.map(({ results }) => results)?.flat();

  const initialStringified =
    !!initialSelectedItemIds.length && JSON.stringify(initialSelectedItemIds);
  const selectedStringified = JSON.stringify(selectedItems);

  // Fetch the contents of the items initially selected.
  // This is used to show the selected contents preview up top,
  // since they might not be fetched yet due to pagination
  useEffect(() => {
    if (initialStringified) {
      // eslint-disable-next-line no-inner-declarations
      async function fetchData() {
        const response = await fetchInitialContentFn(
          initialSelectedItemIds,
          creatorId,
        );

        setSelectedContent(response);
      }
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStringified]);

  // Update the list of selected contents whenever the selected items change.
  useEffect(() => {
    setSelectedContent(
      selectedItems
        .map((id) => {
          // If the selected item is already visible on the stories list, return that content
          // else return it from the already selected contents
          return (
            reels.find((item) => item.id === id) ||
            selectedContent.find((selectedItem) => selectedItem.id === id)
          );
        })
        .filter((item) => !!item),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStringified]);

  useEffect(() => {
    if (initialSelectedItemIds) {
      setSelectedItems(initialSelectedItemIds);
    }
  }, [initialSelectedItemIds, setSelectedItems]);

  const handleSubmitClick = useCallback(async () => {
    setIsSubmitting(true);
    await onSubmit(
      selectedContent,
      CONTENT_TYPES.reels,
      SOCIAL_PLATFORMS.instagram,
    );

    if (isMounted()) {
      setSelectedItems(initialSelectedItemIds);
      setIsSubmitting(false);
    }
  }, [isMounted, initialSelectedItemIds, onSubmit, selectedContent]);

  const handleItemClick = useCallback(
    (id) => {
      let updatedItems;

      if (!selectedItems.includes(id)) {
        // adding
        if (selectedItems.length >= maxItems) {
          // do nothing if max reached
          return false;
        } else {
          updatedItems = [...selectedItems, id];
        }
      } else {
        //removing
        updatedItems = selectedItems.filter((selected) => selected !== id);
      }

      // update
      setSelectedItems(updatedItems);
    },
    [selectedItems, setSelectedItems, maxItems],
  );

  const resetSelectedItems = useCallback(
    () => setSelectedItems(initialSelectedItemIds),
    [initialSelectedItemIds, setSelectedItems],
  );

  const handleClose = useCallback(() => {
    resetSelectedItems();
    onClose();
  }, [resetSelectedItems, onClose]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      resetSelectedItems();
      onCancel();
    } else {
      handleClose();
    }
  }, [resetSelectedItems, onCancel, handleClose]);

  const handleDateChange = useCallback(
    (newValues) => {
      setStartDate(newValues.startDate);
      setEndDate(newValues.endDate);
    },
    [setStartDate, setEndDate],
  );

  const count = selectedItems.length;

  const isSubmitDisabled = count < minItems || count > maxItems;
  const previewError =
    count < minItems
      ? `You have to select at least ${minItems} ${pluralize(
          'reel',
          'reels',
          minItems,
        )}`
      : undefined;

  const handleClearFilters = useCallback(() => {
    setStartDate(undefined);
    setEndDate(undefined);
  }, [setStartDate, setEndDate]);

  const filtersBarProps = {
    sortingOptions: sortingOptions,
    sortByValue: sortBy,
    onSortByChange: setSortBy,
    startDate: startDate,
    endDate: endDate,
    onDatesChange: handleDateChange,
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isFullScreenOnMobile>
      <Modal.Header title={titles.modal} />

      {shouldShowSelectionPreview && (
        <SelectedContentPreview
          contentType={CONTENT_TYPES.reels}
          error={previewError}
          maxItems={maxItems}
          minItems={minItems}
          onChange={handleItemClick}
          selectedContent={selectedContent}
          title={titles.selectedPreview}
        />
      )}
      <Flex alignItems="center" justifyContent="space-between">
        {`Choose ${pluralize('one', 'up to %n', maxItems)}:`}

        <OpenFiltersButton
          hasFilterSet={filtersApplied}
          isFiltersBoxVisible={isFiltersBoxVisible}
          onClearFilters={handleClearFilters}
          onClick={() => setIsFiltersBoxVisible(!isFiltersBoxVisible)}
        />
      </Flex>

      {isFiltersBoxVisible && <ModalFiltersBar {...filtersBarProps} />}

      <Modal.Body marginTop={{ base: 4, md: 6 }} overflowY="auto">
        <SelectContentGrid
          columns={{ base: 3, md: 5, lg: 6 }}
          disabledItems={disabledItemIds}
          isDisabled={count === maxItems}
          max={maxItems}
          selectedItems={selectedItems}
        >
          {reels.map((item) => (
            <SelectContentGrid.Item
              key={item.id}
              id={item.id}
              overflow="hidden"
              borderRadius="lg"
              onClick={handleItemClick}
            >
              <StoryPreviewCard story={item} borderRadius="lg" />
            </SelectContentGrid.Item>
          ))}
        </SelectContentGrid>
        {(status === 'loading' || isFetchingNextPage) && <Spinner centered />}
        {status === 'success' && filtersApplied && reels.length === 0 && (
          <ModalNoResults />
        )}
        {hasNextPage && (
          <Waypoint
            bottomOffset={-10}
            key={hasNextPage}
            onEnter={() => fetchNextPage()}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <LinkButton fontSize="md" colorScheme="primary" onClick={handleCancel}>
          {cancelButtonText}
        </LinkButton>
        <Button
          colorScheme="secondary"
          isDisabled={isSubmitDisabled}
          isLoading={isSubmitting}
          onClick={handleSubmitClick}
        >
          {confirmButtonText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

SelectReelsModal.propTypes = propTypes;
